import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle, ArrowLeft, CalendarCheck, EnvelopeSimple } from "@phosphor-icons/react";

const STEPS = [
  { icon: EnvelopeSimple, t: "Check your inbox", d: "A calendar invite and confirmation are on their way." },
  { icon: CalendarCheck, t: "We'll come prepared", d: "We'll review your goals so the call is all signal, no fluff." },
];

export default function ThankYou() {
  useEffect(() => {
    window.scrollTo(0, 0);
    if (typeof window !== "undefined" && typeof window.fbq === "function") {
      window.fbq("track", "Schedule");
      window.fbq("track", "Lead");
    }
  }, []);

  return (
    <div className="relative min-h-screen bg-[#05050c] flex flex-col" data-testid="thank-you-page">
      <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(circle_at_50%_0%,rgba(64,224,208,0.16),transparent_45%),radial-gradient(circle_at_80%_70%,rgba(139,92,246,0.14),transparent_45%)]" />

      <header className="relative z-10 border-b border-white/10">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="thanks-logo-home">
            <img src="/mobyle-logo.png" alt="Mobyle" className="h-9 w-auto object-contain" />
            <span className="font-heading font-extrabold text-xl tracking-tight text-white">Mobyle</span>
          </Link>
          <Link
            to="/"
            className="group inline-flex items-center gap-2 text-sm text-white/60 hover:text-teal transition-colors duration-300"
            data-testid="thanks-back-home"
          >
            <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform duration-300" />
            Back to home
          </Link>
        </div>
      </header>

      <main className="relative z-10 flex-1 flex items-center justify-center px-6 py-20">
        <div className="max-w-2xl w-full text-center">
          <motion.div
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", stiffness: 200, damping: 14 }}
            className="mx-auto h-24 w-24 rounded-full bg-teal/10 border border-teal/30 flex items-center justify-center shadow-glow-teal"
          >
            <CheckCircle size={56} weight="fill" className="text-teal" />
          </motion.div>

          <motion.h1
            initial={{ y: 24, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.15, duration: 0.6 }}
            className="mt-8 font-heading font-extrabold tracking-tighter text-white text-4xl sm:text-5xl md:text-6xl leading-[0.98]"
          >
            You&apos;re all set.
          </motion.h1>

          <motion.p
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.25, duration: 0.6 }}
            className="mt-6 text-white/65 leading-relaxed max-w-lg mx-auto"
          >
            Your free build consult is booked. We can&apos;t wait to hear what you
            want to build — and to show you what custom can really do.
          </motion.p>

          <div className="mt-12 grid sm:grid-cols-2 gap-4 text-left">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              return (
                <motion.div
                  key={s.t}
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.35 + i * 0.1, duration: 0.5 }}
                  className="rounded-2xl border border-white/10 bg-white/[0.03] p-6"
                >
                  <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-teal/25 to-cobalt/10 border border-teal/30 text-teal flex items-center justify-center">
                    <Icon size={22} weight="duotone" />
                  </div>
                  <h3 className="mt-4 font-heading font-bold text-white text-lg">{s.t}</h3>
                  <p className="mt-1.5 text-sm text-white/55 leading-relaxed">{s.d}</p>
                </motion.div>
              );
            })}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6, duration: 0.6 }}
          >
            <Link
              to="/"
              className="mt-12 inline-flex items-center gap-2 font-semibold text-black bg-teal rounded-full px-7 py-3.5 hover:bg-teal-dark transition-colors duration-300"
              data-testid="thanks-home-button"
            >
              <ArrowLeft size={18} weight="bold" />
              Back to Mobyle
            </Link>
          </motion.div>
        </div>
      </main>
    </div>
  );
}
